import React, {useCallback, useEffect, useMemo, useState} from "react";
import Chart from "react-apexcharts";
import {RefreshCw} from "lucide-react";
import {toast} from "react-toastify";
import {FlyoutSuperUserNavigationMenu} from "@/components/admin/FlyoutSuperUserNavigationMenu";
import {useAxiosPrivate} from "@/hooks/useAxiosPrivate";
import {getAnalyticsDashboardApi} from "@/service/admin/analyticsApi";

const chartRed = "#A20000";
const apexFontFamily = "Figtree-Regular, NotoSerifDevanagari-Regular, sans-serif";
const apexTextStyle = {fontFamily: apexFontFamily};
const apexFontOptions = {
	chart: {fontFamily: apexFontFamily},
	dataLabels: {style: apexTextStyle},
	legend: {fontFamily: apexFontFamily},
	tooltip: {style: {fontFamily: apexFontFamily}},
	title: {style: apexTextStyle},
	subtitle: {style: apexTextStyle},
	xaxis: {
		labels: {style: apexTextStyle},
		title: {style: apexTextStyle},
	},
	yaxis: {
		labels: {style: apexTextStyle},
		title: {style: apexTextStyle},
	},
};

export const Analytics = ({selectedMenuOption, setSelectedMenuOption}) => {
	const axiosPrivate = useAxiosPrivate();
	const [dashboard, setDashboard] = useState(null);
	const [loading, setLoading] = useState(false);
	const [geoView, setGeoView] = useState("city");
	const [chartRevision, setChartRevision] = useState(0);

	const loadDashboard = useCallback(async () => {
		setLoading(true);
		try {
			const response = await getAnalyticsDashboardApi(axiosPrivate);
			setDashboard(response.data);
			setChartRevision((revision) => revision + 1);
		} catch {
			toast.error("Failed to load analytics.");
		} finally {
			setLoading(false);
		}
	}, [axiosPrivate]);

	useEffect(() => {
		loadDashboard();
	}, [loadDashboard]);

	const kpis = dashboard?.kpis ?? {};
	const claimsByStatus = useMemo(() => {
		const colors = {
			PENDING: "#d97706",
			NO_MATCH: "#dc2626",
			NO_CONTACT: "#f97316",
			ALREADY_REGISTERED: "#2563eb",
			AUTO_RESOLVED: "#059669",
			RESOLVED: "#10b981",
			REJECTED: "#6b7280",
		};
		const data = dashboard?.profileClaimsFunnel ?? [];
		return {
			labels: data.map((item) => item.status),
			series: [{name: "Claims", data: data.map((item) => item.count)}],
			colors: data.map((item) => colors[item.status] ?? "#6b7280"),
		};
	}, [dashboard]);
	const registrationTrendChart = useMemo(() => {
		const data = dashboard?.registrationTrend ?? [];
		return {
			series: [{
				name: "Registrations",
				data: data.map((item) => Number(item.count ?? 0)),
			}],
			options: {
				...apexFontOptions,
				chart: {
					...apexFontOptions.chart,
					id: "analytics-registration-trend",
					toolbar: {show: false},
					zoom: {enabled: false},
				},
				colors: [chartRed],
				dataLabels: {...apexFontOptions.dataLabels, enabled: false},
				stroke: {curve: "smooth", width: 2},
				fill: {
					type: "gradient",
					gradient: {shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05},
				},
				xaxis: {
					...apexFontOptions.xaxis,
					categories: data.map((item) => item.month ?? ""),
				},
				yaxis: {...apexFontOptions.yaxis, forceNiceScale: true},
			},
		};
	}, [dashboard?.registrationTrend]);
	const loginActivityChart = useMemo(() => {
		const data = dashboard?.loginSparkline ?? [];
		return {
			series: [{
				name: "Logins",
				data: data.map((item) => Number(item.count ?? 0)),
			}],
			options: {
				...apexFontOptions,
				chart: {
					...apexFontOptions.chart,
					id: "analytics-login-activity",
					toolbar: {show: false},
					sparkline: {enabled: true},
				},
				colors: [chartRed],
				stroke: {curve: "smooth", width: 2},
				tooltip: {...apexFontOptions.tooltip, x: {show: true}},
				xaxis: {
					...apexFontOptions.xaxis,
					categories: data.map((item) => item.date ?? ""),
				},
				yaxis: {...apexFontOptions.yaxis, forceNiceScale: true},
			},
		};
	}, [dashboard?.loginSparkline]);

	return (
		<div
			className="flex h-full flex-col gap-3 overflow-hidden
                portrait:p-[0.75rem] portrait:jk:p-[1.25rem]
                landscape:px-[1rem] landscape:py-[0.75rem]
                landscape:jk:px-[1rem] landscape:jk:py-[0.75rem]"
		>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="flex items-center gap-2">
					<FlyoutSuperUserNavigationMenu
						selectedMenuOption={selectedMenuOption}
						setSelectedMenuOption={setSelectedMenuOption}
					/>
					<div>
						<h1
							className="font-custom-bold leading-tight
                            portrait:text-[1.5rem] portrait:md:text-[1.375rem]
                            portrait:jk:text-[1.5rem] portrait:lg:text-[2rem]
                            landscape:text-[0.9rem] landscape:md:text-[1rem]
                            landscape:jk:text-[1.1rem] landscape:lg:text-[1.5rem]"
						>
							Analytics
						</h1>
						<p className="font-custom-regular text-sm text-gray-500">Living, non-outsider profile
							metrics.</p>
					</div>
				</div>
				<div className="flex flex-col items-end gap-1">
					<button
						type="button"
						onClick={loadDashboard}
						disabled={loading}
						className="inline-flex items-center gap-2 rounded-lg bg-gray-950 px-3 py-2 text-sm font-custom-semi-bold text-white disabled:bg-gray-300"
					>
						<RefreshCw size={16} className={loading ? "animate-spin" : ""}/>
						Refresh
					</button>
					{dashboard?.lastUpdated && (
						<p className="font-custom-regular text-xs text-gray-500">Last
							updated: {new Date(dashboard.lastUpdated).toLocaleString("en-IN")}</p>
					)}
				</div>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto pr-1">
				{loading && !dashboard ? (
					<div className="flex h-full items-center justify-center font-custom-medium text-gray-500">Loading
						analytics...</div>
				) : (
					<div className="space-y-3">
						<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
							<KpiCard title="Total Profiles" value={kpis.totalProfiles} subtitle="Living, non-outsider"/>
							<KpiCard title="Verified Members" value={kpis.verifiedCount}
							         subtitle={`${kpis.verifiedPercent ?? 0}% of total`}/>
							<KpiCard title="Registered" value={kpis.pendingCount} subtitle="Has phone + email"/>
							<KpiCard title="Unregistered" value={kpis.unregisteredCount}
							         subtitle="Has phone or email only"/>
							<KpiCard title="Pending Claims" value={kpis.pendingClaimsCount}
							         subtitle="Awaiting super admin"/>
						</div>

						<ChartPanel title="Registration Trend">
							<Chart
								key={`registration-trend-${chartRevision}`}
								type="area"
								height={280}
								series={registrationTrendChart.series}
								options={registrationTrendChart.options}
							/>
						</ChartPanel>

						<div className="grid gap-3 xl:grid-cols-2">
							<ChartPanel title="Generation Distribution">
								<Chart
									type="bar"
									height={300}
									series={[{
										name: "Profiles",
										data: dashboard?.generationDistribution?.map((item) => item.count) ?? []
									}]}
									options={{
										...apexFontOptions,
										chart: {...apexFontOptions.chart, toolbar: {show: false}},
										plotOptions: {bar: {horizontal: true, borderRadius: 4}},
										colors: [chartRed],
										dataLabels: {...apexFontOptions.dataLabels, enabled: false},
										xaxis: {
											...apexFontOptions.xaxis,
											categories: dashboard?.generationDistribution?.map((item) => `Generation ${item.generation}`) ?? []
										},
									}}
								/>
							</ChartPanel>

							<ChartPanel
								title="Geographic Spread"
								action={(
									<div className="flex rounded-lg bg-gray-100 p-1">
										{["city", "state", "country"].map((item) => (
											<button
												key={item}
												type="button"
												onClick={() => setGeoView(item)}
												className={`rounded-md px-2.5 py-1 text-xs font-custom-bold capitalize ${geoView === item ? "bg-white text-[#A20000] shadow-sm" : "text-gray-600"}`}
											>
												{item}
											</button>
										))}
									</div>
								)}
							>
								<Chart
									type="bar"
									height={300}
									series={[{
										name: "Profiles",
										data: dashboard?.geographicSpread?.[geoView]?.map((item) => item.count) ?? []
									}]}
									options={{
										...apexFontOptions,
										chart: {...apexFontOptions.chart, toolbar: {show: false}},
										plotOptions: {bar: {borderRadius: 4}},
										colors: ["#D26A53"],
										dataLabels: {...apexFontOptions.dataLabels, enabled: false},
										xaxis: {
											...apexFontOptions.xaxis,
											categories: dashboard?.geographicSpread?.[geoView]?.map((item) => item.label) ?? []
										},
									}}
								/>
							</ChartPanel>
						</div>

						<div className="grid gap-3 xl:grid-cols-[minmax(0,1.8fr)_minmax(420px,1fr)]">
							<ChartPanel title="Profile Claims Funnel" className="min-h-[520px]">
								<Chart
									type="bar"
									height={456}
									series={claimsByStatus.series}
									options={{
										...apexFontOptions,
										chart: {...apexFontOptions.chart, toolbar: {show: false}},
										plotOptions: {bar: {horizontal: true, borderRadius: 5, distributed: true, barHeight: "48%"}},
										colors: claimsByStatus.colors,
										dataLabels: {...apexFontOptions.dataLabels, enabled: false},
										xaxis: {...apexFontOptions.xaxis, categories: claimsByStatus.labels},
										legend: {...apexFontOptions.legend, show: false},
										grid: {padding: {left: 8, right: 18}},
									}}
								/>
							</ChartPanel>

							<div className="grid min-h-[520px] gap-3 xl:grid-rows-[minmax(0,0.52fr)_minmax(0,0.48fr)]">
								<div className="grid gap-3 sm:grid-cols-2">
									<ChartPanel title="Claim Conversion" compact className="min-h-[238px]">
										<div className="flex h-[182px] items-center justify-center">
											<Chart
												type="radialBar"
												height={196}
												width="100%"
												series={[dashboard?.claimsConversionRate ?? 0]}
												options={{
													...apexFontOptions,
													colors: [chartRed],
													labels: ["Conversion Rate"],
													plotOptions: {
														radialBar: {
															hollow: {size: "58%"},
															dataLabels: {
																name: {
																	fontFamily: apexFontFamily,
																	fontSize: "13px",
																	offsetY: -4,
																},
																value: {
																	fontFamily: apexFontFamily,
																	fontSize: "18px",
																	offsetY: 4,
																	formatter: (value) => `${Math.round(value)}%`,
																},
															}
														}
													},
												}}
											/>
										</div>
									</ChartPanel>

									<ChartPanel title="Active Sessions" compact className="min-h-[238px]">
										<div className="flex h-[182px] flex-col justify-center">
											<p className="font-custom-black text-5xl leading-none text-gray-950">{dashboard?.activeSessions ?? 0}</p>
											<p className="mt-4 max-w-[14rem] font-custom-regular text-sm leading-snug text-gray-500">
												Distinct users with unexpired active sessions.
											</p>
										</div>
									</ChartPanel>
								</div>

								<ChartPanel title="Login Activity" compact className="min-h-[258px]">
									<Chart
										key={`login-activity-${chartRevision}`}
										type="line"
										height={198}
										series={loginActivityChart.series}
										options={{
											...loginActivityChart.options,
											chart: {
												...loginActivityChart.options.chart,
												sparkline: {enabled: false},
											},
											grid: {
												borderColor: "#f1f5f9",
												padding: {left: 12, right: 16, top: 4, bottom: 0},
											},
											xaxis: {
												...loginActivityChart.options.xaxis,
												labels: {
													...apexFontOptions.xaxis.labels,
													show: true,
													rotate: 0,
													hideOverlappingLabels: true,
												},
											},
											yaxis: {
												...loginActivityChart.options.yaxis,
												labels: {...apexFontOptions.yaxis.labels, show: true},
											},
											markers: {size: 3, strokeWidth: 0},
										}}
									/>
								</ChartPanel>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

const KpiCard = ({title, value = 0, subtitle}) => (
	<div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
		<div className="mb-3 h-1 w-12 rounded-full bg-[#A20000]"/>
		<p className="font-custom-black text-3xl text-gray-950">{Number(value ?? 0).toLocaleString("en-IN")}</p>
		<p className="mt-2 font-custom-bold text-sm text-gray-800">{title}</p>
		<p className="font-custom-regular text-xs text-gray-500">{subtitle}</p>
	</div>
);

const ChartPanel = ({title, action, children, compact = false, className = ""}) => (
	<section className={`rounded-xl border border-gray-200 bg-white shadow-sm ${compact ? "p-3" : "p-4"} ${className}`}>
		<div className={`${compact ? "mb-2" : "mb-3"} flex items-center justify-between gap-3`}>
			<h2 className="font-custom-bold text-base text-gray-950">{title}</h2>
			{action}
		</div>
		{children}
	</section>
);
